import {
  ProntuarioResponseEvolucao,
  SincronizarTratamentoDtoMedicamentosItemFrequencia,
  SincronizarTratamentoDtoMedicamentosItemVia,
} from '@/api/generated/model';

/**
 * Rótulos e formatação do tratamento (§3.4 e §10.3 da spec).
 *
 * Fica ao lado de `rotulos.ts` — que é do prontuário — e não dentro dele: o
 * tratamento aparece em DOIS lugares (o editor no prontuário e a aba
 * Tratamentos), e uma segunda cópia de "como se escreve BID" divergiria na
 * primeira correção.
 */

export const FREQUENCIAS = Object.values(SincronizarTratamentoDtoMedicamentosItemFrequencia);

/**
 * A sigla é o que o veterinário escreve; o texto entre parênteses é para quem
 * ainda não a decorou. Nenhuma das duas some.
 */
export const ROTULO_FREQUENCIA: Record<string, string> = {
  SID: 'SID (1×/dia)',
  BID: 'BID (2×/dia)',
  TID: 'TID (3×/dia)',
  EOD: 'EOD (dias alternados)',
  SEMANAL: 'Semanal',
  MENSAL: 'Mensal',
  OUTRA: 'Outra',
};

export const VIAS = Object.values(SincronizarTratamentoDtoMedicamentosItemVia).filter(
  (v): v is Exclude<SincronizarTratamentoDtoMedicamentosItemVia, null> => v !== null,
);

export const ROTULO_VIA: Record<string, string> = {
  ORAL: 'Oral',
  SUBCUTANEA: 'Subcutânea',
  INTRAMUSCULAR: 'Intramuscular',
  INTRAVENOSA: 'Intravenosa',
  TOPICA: 'Tópica',
  OFTALMICA: 'Oftálmica',
  OTOLOGICA: 'Otológica',
  INALATORIA: 'Inalatória',
  OUTRA: 'Outra',
};

export const EVOLUCOES = Object.values(ProntuarioResponseEvolucao).filter(
  (v): v is Exclude<ProntuarioResponseEvolucao, null> => v !== null,
);

export const ROTULO_EVOLUCAO: Record<string, string> = {
  MELHOROU: 'Melhora Clínica',
  ESTAVEL: 'Estável',
  PIOROU: 'Agravamento Clínico',
};

const NUMERO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 });

export function formatarNumero(valor: number): string {
  return NUMERO.format(valor);
}

/**
 * Texto digitado → número. Aceita vírgula, que é o que o teclado brasileiro
 * entrega: `1,5` digitado e lido como `1` seria erro de dose, não de máscara.
 */
export function paraNumero(texto: string): number {
  return Number(texto.trim().replace(',', '.'));
}

/** Número → texto do campo, na vírgula de quem vai reler. */
export function numeroParaCampo(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '' : String(valor).replace('.', ',');
}

/**
 * Dia de calendário (`@db.Date`, `AAAA-MM-DD`) formatado em **UTC**.
 *
 * ⚠️ `new Date('2026-03-01')` é meia-noite UTC; lido no fuso local de São Paulo
 * ele volta como 28/02. Vigência é dia, não instante — em três meses de
 * histórico de dose, um dia a menos em cada linha muda o período inteiro. É a
 * mesma razão do `formatarData` de `features/exames/rotulos.ts`.
 */
export function formatarDia(dia: string): string {
  return new Date(dia).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function pedacos(dia: string): { curto: string; ano: string } {
  const completo = formatarDia(dia);
  return { curto: completo.slice(0, 5), ano: completo.slice(6) };
}

/**
 * Período de uma vigência, como a spec desenha: `01/01–28/02`, e `01/05–atual`
 * na última.
 *
 * ⚠️ `vigenteAte: null` é a dose de HOJE — nunca "sem fim conhecido" e nunca
 * em branco. O backend já entrega o último dia calculado (o anterior ao início
 * da próxima); a tela só escolhe a palavra.
 *
 * O ano só aparece quando o período atravessa a virada: dentro do mesmo ano ele
 * é ruído em cima de uma tabela que já é densa.
 */
export function formatarPeriodo(vigenteDe: string, vigenteAte: string | null): string {
  const de = pedacos(vigenteDe);
  if (vigenteAte === null) return `${de.curto}/${de.ano}–atual`;

  const ate = pedacos(vigenteAte);
  return de.ano === ate.ano
    ? `${de.curto}–${ate.curto}/${ate.ano}`
    : `${de.curto}/${de.ano}–${ate.curto}/${ate.ano}`;
}

interface DoseExibivel {
  doseValor: number;
  doseUnidade: string;
  dosePorKg: number | null;
}

/**
 * A dose como a coluna da spec a mostra: `1 mg/kg` quando há dose/kg gravada,
 * senão a dose por administração.
 */
export function descreverDose(dose: DoseExibivel): string {
  return dose.dosePorKg !== null
    ? `${formatarNumero(dose.dosePorKg)} ${dose.doseUnidade}/kg`
    : `${formatarNumero(dose.doseValor)} ${dose.doseUnidade}`;
}

/** A dose por administração, sempre — vai como apoio quando há dose/kg. */
export function descreverDoseAbsoluta(dose: DoseExibivel): string {
  return `${formatarNumero(dose.doseValor)} ${dose.doseUnidade}`;
}

export function descreverFrequencia(frequencia: string, frequenciaLivre: string | null): string {
  if (frequencia === 'OUTRA') return frequenciaLivre?.trim() || 'Outra';
  return ROTULO_FREQUENCIA[frequencia] ?? frequencia;
}

/**
 * Dose/kg sugerida a partir do peso corrente.
 *
 * ⚠️ É SUGESTÃO, e o campo continua editável. O valor é GRAVADO na vigência,
 * não recalculado na leitura: o peso muda de uma consulta para a outra, e a
 * dose/kg de março tem de continuar descrevendo a exposição de março.
 */
export function sugerirDosePorKg(doseValor: number, pesoKg: number | null): number | null {
  if (!pesoKg || pesoKg <= 0 || !Number.isFinite(doseValor) || doseValor <= 0) return null;
  return Math.round((doseValor / pesoKg) * 10_000) / 10_000;
}

/**
 * `atendidoEm` (instante ISO em UTC) → o dia da vigência (`AAAA-MM-DD`).
 *
 * ⚠️ Corta em UTC de propósito: é assim que o backend deriva a data da
 * vigência do mesmo `atendidoEm` que recebe. Cortar no fuso local aqui faria o
 * `emData` da pré-carga apontar para um dia e a vigência gravada para outro —
 * e o diff acusaria mudança onde não houve.
 */
export function diaDoAtendimento(atendidoEmISO: string): string {
  return atendidoEmISO.slice(0, 10);
}

/** O aviso que a spec exige que a tela não esconda. */
export const AVISO_NAO_PADRONIZADO =
  'Fármaco fora do catálogo: não entra em filtro por fármaco na Análise Comparativa.';
